import { driver, type Driver, type DriveStep } from 'driver.js';
// Imported here rather than in a global stylesheet, so only the chunk that actually runs
// tours pays for it and no non-depot page loads it.
import 'driver.js/dist/driver.css';
// After driver.css, so these win. See the file for what they fix.
import '@/assets/css/driver-overrides.css';

import router from '@/router';
import { canVisit } from '@/composables/useCanVisit';
import { SHELL_TOUR, tourForRoute, type Tour, type TourStep } from '@/config/tours';
import {
	autoStartsRemaining,
	hasSeen,
	isMuted,
	markSeen,
	muteAll,
	recordAutoStart,
} from '@/util/tourSeen';

/** How long to wait for a page to render the anchors its tour needs. */
const ANCHOR_TIMEOUT_MS = 2500;

const selectorFor = (anchor: string) => {
	return `[data-tour="${anchor}"]`;
};

const anchorExists = (anchor: string): boolean => {
	return document.querySelector(selectorFor(anchor)) !== null;
};

/**
 * A step survives when the operator can reach wherever it points. Steps that point at
 * nothing — a description of the page rather than of a link — always survive here, and are
 * filtered later on whether their anchor actually rendered.
 */
const reachable = (step: TourStep): boolean => {
	return !step.route || canVisit(step.route);
};

/**
 * Resolve after every one of these anchors exists, or after the timeout.
 *
 * Returns whether they all appeared, because the two outcomes are treated differently: a
 * tour that ran is marked seen, a tour that timed out is not.
 */
const waitForAnchors = (anchors: string[]): Promise<boolean> => {
	if (anchors.every(anchorExists)) {
		return Promise.resolve(true);
	}

	return new Promise((resolve) => {
		let settled = false;

		const finish = (ok: boolean) => {
			if (settled) {
				return;
			}

			settled = true;
			observer.disconnect();
			window.clearTimeout(timer);
			resolve(ok);
		};

		const observer = new MutationObserver(() => {
			if (anchors.every(anchorExists)) {
				finish(true);
			}
		});

		const timer = window.setTimeout(() => {
			return finish(false);
		}, ANCHOR_TIMEOUT_MS);

		observer.observe(document.body, { childList: true, subtree: true });
	});
};

let active: Driver | null = null;

/** Stop whatever is running. Called on route change and before starting anything new. */
export const stopTour = (): void => {
	if (active) {
		active.destroy();
		active = null;
	}
};

const toDriveSteps = (steps: TourStep[]): DriveStep[] => {
	return steps.map((step) => {
		return {
			element: selectorFor(step.anchor),
			popover: { title: step.title, description: step.body },
		};
	});
};

/**
 * Run a sequence of tours as one continuous flow.
 *
 * A tour is marked seen as soon as any of its steps has been shown. Seeing the start of a
 * tour and closing it is a decision about that tour, so re-showing it would ignore the
 * decision; but a tour further down a chain that was never reached keeps its chance.
 *
 * That distinction is what makes muting safe. Muting suppresses tours rather than
 * dismissing them, so a tour the operator never saw must not be marked seen on the way
 * out, or unmuting later would silently skip it forever.
 */
const drive = (tours: Tour[], steps: TourStep[]): Promise<void> => {
	return new Promise((resolve) => {
		const ownerOf = (step: TourStep): Tour | undefined => {
			return tours.find((t) => {
				return t.steps.includes(step);
			});
		};

		const finish = () => {
			active = null;
			resolve();
		};

		active = driver({
			showProgress: true,
			allowClose: true,
			steps: toDriveSteps(steps),
			onHighlightStarted: (_el, _step, opts) => {
				const shown = steps[opts.state.activeIndex ?? 0];
				const owner = shown ? ownerOf(shown) : undefined;
				if (owner) {
					markSeen(owner.key, owner.version);
				}
			},
			// The escape that makes a tour on every page tolerable. Muting stops every
			// automatic tour everywhere; the topbar control is how it is undone. Nothing is
			// marked seen here beyond what was already shown.
			onPopoverRender: (popover) => {
				// Appended to the footer rather than to footerButtons. That group is
				// `flex-grow: 1`, so a control inside it squeezes the "1 of 12" progress text
				// until it wraps. In the footer it takes its own row instead, which also suits
				// a decision that is bigger than "next".
				if (popover.footer.querySelector('.driver-popover-mute-btn')) {
					return;
				}

				const button = document.createElement('button');
				button.type = 'button';
				button.textContent = "Don't show these again";
				button.className = 'driver-popover-mute-btn';
				button.addEventListener('click', () => {
					muteAll();
					stopTour();
					finish();
				});
				popover.footer.appendChild(button);
			},
			onDestroyed: finish,
		});

		active.drive();
	});
};

/**
 * Build the step list for a set of tours: drop steps pointing where the operator cannot go,
 * then drop steps whose anchor did not render.
 *
 * The two filters run either side of the wait, because they answer different questions.
 * Reachability is knowable immediately. Presence is only meaningful once the page has had
 * its chance to render, which is what the wait is for.
 */
const resolveSteps = async (tours: Tour[]): Promise<TourStep[] | null> => {
	const reachableSteps = tours.flatMap((t) => {
		return t.steps.filter(reachable);
	});

	if (!reachableSteps.length) {
		return [];
	}

	// The first anchor of EACH tour, not of the sequence. The shell tour's first anchor is
	// the sidebar, which exists immediately, so waiting on the sequence alone would let a
	// chained page tour start before its page had rendered.
	const firstAnchors = tours
		.map((t) => {
			return t.steps.filter(reachable)[0]?.anchor;
		})
		.filter((a): a is string => {
			return Boolean(a);
		});

	const appeared = await waitForAnchors(firstAnchors);
	if (!appeared) {
		return null;
	}

	return reachableSteps.filter((step) => {
		return anchorExists(step.anchor);
	});
};

const run = async (tours: Tour[], isAuto: boolean): Promise<void> => {
	stopTour();

	const steps = await resolveSteps(tours);

	// Timed out waiting for the page. Skipped silently and deliberately NOT marked seen:
	// one slow response should not cost a page its tour for good.
	if (steps === null) {
		return;
	}

	// Nothing left to show this operator. Marked seen, because retrying on every visit
	// would only repeat the same empty result.
	if (!steps.length) {
		for (const tour of tours) {
			markSeen(tour.key, tour.version);
		}

		return;
	}

	if (isAuto) {
		recordAutoStart();
	}

	await drive(tours, steps);
};

/**
 * Development-time drift check. The registry names anchors as strings and the markup carries
 * them as attributes, with nothing keeping the two in step — the same failure shape as a
 * link disagreeing with its route guard, which this codebase answers with a DOM sweep rather
 * than a test. A renamed or deleted anchor is reported the moment someone opens the page.
 *
 * Steps filtered out by `canVisit` are skipped, since their anchor is legitimately absent.
 */
const sweepAnchors = (routeName: string | null | undefined): void => {
	if (!import.meta.env.DEV) {
		return;
	}

	// Deferred by the same window the tours themselves wait out. Sweeping on mount would
	// report every anchor on every page whose content arrives asynchronously, which is most
	// of them, and a check that cries wolf is a check nobody reads.
	window.setTimeout(() => {
		if (router.currentRoute.value.name !== routeName) {
			return;
		}

		sweepNow(routeName);
	}, ANCHOR_TIMEOUT_MS);
};

const sweepNow = (routeName: string | null | undefined): void => {
	const tours: Tour[] = [SHELL_TOUR];
	const pageTour = tourForRoute(routeName);
	if (pageTour) {
		tours.push(pageTour);
	}

	for (const tour of tours) {
		for (const step of tour.steps) {
			if (!reachable(step) || anchorExists(step.anchor)) {
				continue;
			}

			// eslint-disable-next-line no-console
			console.warn(
				`[tours] ${tour.key}: no element carries data-tour="${step.anchor}" on this page`,
			);
		}
	}
};

export const useDepotTour = () => {
	/**
	 * Called on arrival at every depot route. Runs the shell tour when it has not been seen,
	 * chained into this page's tour when that has not been seen either.
	 */
	const maybeAutoStart = async (routeName: string | null | undefined): Promise<void> => {
		if (isMuted() || autoStartsRemaining() <= 0) {
			// Not marked seen: muting is reversible and the cap resets next session, so both
			// of these tours should still be offered later.
			return;
		}

		const tours: Tour[] = [];

		if (!hasSeen(SHELL_TOUR.key, SHELL_TOUR.version)) {
			tours.push(SHELL_TOUR);
		}

		const pageTour = tourForRoute(routeName);
		if (pageTour && !hasSeen(pageTour.key, pageTour.version)) {
			tours.push(pageTour);
		}

		if (!tours.length) {
			return;
		}

		await run(tours, true);
	};

	/**
	 * The topbar control. Ignores seen state, muting and the cap alike, and consumes no cap
	 * slot: it is the way back after any of those, so it cannot be subject to them.
	 */
	const runNow = async (routeName: string | null | undefined): Promise<void> => {
		const pageTour = tourForRoute(routeName);
		if (!pageTour) {
			return;
		}

		await run([pageTour], false);
	};

	const hasTour = (routeName: string | null | undefined): boolean => {
		return Boolean(tourForRoute(routeName));
	};

	return { maybeAutoStart, runNow, hasTour, stopTour, sweepAnchors };
};
